import React from "react";

// SIGNAL COMPARE — dual-marker Taste Match visual. Places BOTH players on the
// same five displayed Taste Signal spectra so alignment/divergence reads at a
// glance. Same locked TT grammar (bipolar tracks, delta markers, no numbers).
// YOU = filled bone delta. THEM = hollow delta. A faint connector spans the gap.

function pctOf(v) {
  const c = Math.max(-1, Math.min(1, v || 0));
  return 8 + ((c + 1) / 2) * 84;
}

function Row({ row }) {
  const you = pctOf(row.youValue);
  const them = row.themValue == null ? null : pctOf(row.themValue);
  const lo = them == null ? you : Math.min(you, them);
  const hi = them == null ? you : Math.max(you, them);
  return (
    <div className="select-none" data-testid={`signal-compare-row-${row.key}`}>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="font-mono text-[9.5px] tracking-[0.16em] uppercase leading-none text-bone/70">
          {row.lowLabel}
        </span>
        <span className="font-mono text-[9.5px] tracking-[0.16em] uppercase leading-none text-right text-bone/70">
          {row.highLabel}
        </span>
      </div>
      <div
        className="relative h-[6px] rounded-full"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,42,85,0.30), rgba(255,255,255,0.09) 50%, rgba(0,229,255,0.30))",
        }}
      >
        {them != null && (
          <span
            aria-hidden
            className="absolute top-1/2 -translate-y-1/2 h-[2px] rounded-full"
            style={{ left: `${lo}%`, width: `${hi - lo}%`, background: "rgba(244,241,234,0.28)" }}
          />
        )}
        {them != null && (
          <span
            aria-hidden
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${them}%` }}
          >
            <span className="block h-[11px] w-[11px] rotate-45 rounded-[2px] border border-white/80 bg-canvas" />
          </span>
        )}
        <span
          aria-hidden
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${you}%` }}
        >
          <span className="block h-[11px] w-[11px] rotate-45 rounded-[2px] bg-bone shadow-[0_0_10px_rgba(244,241,234,0.5)]" />
        </span>
      </div>
    </div>
  );
}

export default function SignalCompare({ rows = [], youLabel = "You", themLabel = "Them" }) {
  if (!Array.isArray(rows) || rows.length < 3) return null;
  return (
    <section data-testid="signal-compare">
      <div className="flex items-center justify-between mb-4 gap-3">
        <p className="font-mono text-[10px] font-bold tracking-[0.28em] uppercase text-subtle">
          Where your signals meet
        </p>
        <div className="flex items-center gap-3 font-mono text-[9px] tracking-[0.12em] uppercase text-silver shrink-0">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rotate-45 bg-bone inline-block" />{youLabel}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rotate-45 border border-white/80 inline-block" />
            <span className="max-w-[80px] truncate">{themLabel}</span>
          </span>
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-4 space-y-4">
        {rows.slice(0, 5).map((r) => (
          <Row key={r.key} row={r} />
        ))}
      </div>
    </section>
  );
}
